// Pointer events in CSS pixels relative to an element. Works for finger and mouse.

export interface PointerPos { x: number; y: number; id: number }

export interface PointerHandlers {
  down?(p: PointerPos, e: PointerEvent): void;
  move?(p: PointerPos, e: PointerEvent): void;
  up?(p: PointerPos, e: PointerEvent): void;
  tap?(p: PointerPos, e: PointerEvent): void;   // down + up without much movement
}

export function attachPointer(el: HTMLElement, hs: PointerHandlers): () => void {
  let downAt: PointerPos | null = null;
  const pos = (e: PointerEvent): PointerPos => {
    const b = el.getBoundingClientRect();
    return { x: e.clientX - b.left, y: e.clientY - b.top, id: e.pointerId };
  };
  const onDown = (e: PointerEvent) => {
    el.setPointerCapture?.(e.pointerId);
    const p = pos(e);
    downAt = p;
    hs.down?.(p, e);
  };
  const onMove = (e: PointerEvent) => {
    if (e.buttons === 0 && e.pointerType === 'mouse' && !downAt) return;
    hs.move?.(pos(e), e);
  };
  const onUp = (e: PointerEvent) => {
    const p = pos(e);
    hs.up?.(p, e);
    if (downAt && Math.hypot(p.x - downAt.x, p.y - downAt.y) < 12) hs.tap?.(p, e);
    downAt = null;
  };
  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
  return () => {
    el.removeEventListener('pointerdown', onDown);
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerup', onUp);
    el.removeEventListener('pointercancel', onUp);
  };
}
